<template>
  <div class="mx-10 ma-5">
    <v-card class="pa-5" v-if="receipt && receipt.receipt_no">
      <div>
        <PageTitle :title="'Receipt Details'" class="pb-5"></PageTitle>

        <div class="d-flex justify-end mb-4 mr-4">
          <v-btn color="primary" @click="printReceipt">
            <v-icon left>mdi-printer</v-icon>
            Print
          </v-btn>
        </div>

        <div id="receipt-print-area">
          <!-- Receipt Info -->
          <v-card flat class="mb-5 pa-4" color="grey lighten-4">
            <v-card-title class="text-h5 font-weight-bold pa-0 mb-3">
              <v-icon large color="primary" class="mr-2">mdi-receipt</v-icon>
              Receipt
            </v-card-title>
            <v-divider class="mb-4"></v-divider>
            <v-row>
              <v-col cols="12" md="6">
                <div class="info-row">
                  <span class="label">Receipt Number:</span>
                  <span class="value font-weight-bold">{{
                    receipt.receipt_no
                  }}</span>
                </div>
                <div class="info-row">
                  <span class="label">Date:</span>
                  <span class="value">{{ formatDate(receipt.date) }}</span>
                </div>
                <div class="info-row">
                  <span class="label">Customer:</span>
                  <span class="value">{{ customerName }}</span>
                </div>
              </v-col>
              <v-col cols="12" md="6">
                <div class="info-row">
                  <span class="label">Status:</span>
                  <v-chip
                    small
                    :color="receipt.is_confirmed ? 'green' : 'orange'"
                    text-color="white"
                  >
                    {{ receipt.is_confirmed ? "Confirmed" : "Pending" }}
                  </v-chip>
                </div>
                <div class="info-row" v-if="receipt.remarks">
                  <span class="label">Remarks:</span>
                  <span class="value">{{ receipt.remarks }}</span>
                </div>
              </v-col>
            </v-row>
          </v-card>

          <!-- Invoice Info -->
          <v-card flat class="mb-5 pa-4" v-if="receipt.invoice">
            <v-card-title class="text-h5 font-weight-bold pa-0 mb-3">
              <v-icon large color="primary" class="mr-2"
                >mdi-file-document</v-icon
              >
              Invoice Details
            </v-card-title>
            <v-divider class="mb-4"></v-divider>
            <v-row>
              <v-col cols="12" md="6">
                <div class="info-row">
                  <span class="label">Invoice Number:</span>
                  <span class="value font-weight-bold">{{
                    receipt.invoice.invoice_number
                  }}</span>
                </div>
                <div class="info-row">
                  <span class="label">Invoice Date:</span>
                  <span class="value">{{
                    formatDate(receipt.invoice.invoice_date)
                  }}</span>
                </div>
                <div class="info-row">
                  <span class="label">Due Date:</span>
                  <span class="value">{{
                    formatDate(receipt.invoice.invoice_due_date)
                  }}</span>
                </div>
              </v-col>
              <v-col cols="12" md="6">
                <div class="info-row">
                  <span class="label">Branch:</span>
                  <span class="value">{{
                    receipt.invoice.branch_name || "-"
                  }}</span>
                </div>
                <div class="info-row">
                  <span class="label">Total Invoice:</span>
                  <span class="value font-weight-bold"
                    >{{ currencyCode }}
                    {{ Number(receipt.invoice.total_invoice).toFixed(2) }}</span
                  >
                </div>
                <div class="info-row">
                  <span class="label">Outstanding:</span>
                  <span class="value font-weight-bold red--text"
                    >{{ currencyCode }}
                    {{ Number(outstandingAmount).toFixed(2) }}</span
                  >
                </div>
              </v-col>
            </v-row>
          </v-card>

          
          <!-- Payment Summary -->
          <v-card
            flat
            class="mb-5 pa-4"
            color="grey lighten-4"
            v-if="receipt.invoice"
          >
            <v-card-title class="text-h5 font-weight-bold pa-0 mb-3">
              <v-icon large color="primary" class="mr-2">mdi-calculator</v-icon>
              Payment Summary
            </v-card-title>
            <v-divider class="mb-4"></v-divider>
            <v-row>
              <v-col cols="12" md="4">
                <div class="summary-card text-center pa-4">
                  <div class="summary-label">Invoice Total</div>
                  <div class="summary-value">
                    {{ currencyCode }}
                    {{ Number(receipt.invoice.total_invoice).toFixed(2) }}
                  </div>
                </div>
              </v-col>
              <v-col cols="12" md="4">
                <div class="summary-card text-center pa-4">
                  <div class="summary-label">Amount Paid</div>
                  <div class="summary-value green--text">
                    {{ currencyCode }} {{ Number(receipt.amount).toFixed(2) }}
                  </div>
                </div>
              </v-col>
              <v-col cols="12" md="4">
                <div class="summary-card text-center pa-4">
                  <div class="summary-label">Outstanding</div>
                  <div class="summary-value red--text">
                    {{ currencyCode }}
                    {{ Number(outstandingAmount).toFixed(2) }}
                  </div>
                </div>
              </v-col>
            </v-row>
          </v-card>
        </div>
      </div>
    </v-card>
    <v-alert v-else type="info" class="mt-5" outlined>
      Loading receipt details...
    </v-alert>
  </div>
</template>

<script>
import PageTitle from "@/components/shared/PageTitle";
import { InvoiceService } from "@/services/invoice-service.js";
import { createNamespacedHelpers } from "vuex";
const { mapGetters } = createNamespacedHelpers("settings");

export default {
  name: "ReceiptDetails",
  components: { PageTitle },
  data() {
    return {
      receipt: {},
      itemHeaders: [
        { text: "Code", value: "product_code" },
        { text: "Product", value: "product_name" },
        { text: "Qty", value: "quantity" },
        { text: "Price", value: "selling_price" },
        { text: "Discount", value: "discount" },
        { text: "Total", value: "total_amount" },
      ],
    };
  },
  computed: {
    ...mapGetters(["currencyCode"]),
    customerName() {
      if (!this.receipt.customer) return "-";
      return (
        this.receipt.customer.company_name ||
        this.receipt.customer.first_name + " " + this.receipt.customer.last_name
      );
    },
    outstandingAmount() {
      if (!this.receipt.invoice) return 0;
      const total = Number(this.receipt.invoice.total_invoice) || 0;
      const paid = Number(this.receipt.amount) || 0;
      return total - paid;
    },
  },
  created() {
    this.fetchReceiptDetails();
  },
  methods: {
    fetchReceiptDetails() {
      InvoiceService.getReceiptDetails(this.$route.params.uuid)
        .then((response) => {
          this.receipt = response.data;
        })
        .catch((error) => {
          console.error("Error fetching receipt details:", error);
        });
    },

    printReceipt() {
      let companyName = "";
      try {
        const companySettings = JSON.parse(
          localStorage.getItem("companySettings"),
        );
        if (companySettings && Array.isArray(companySettings.data)) {
          const companyNameSetting = companySettings.data.find(
            (s) => s.key === "company_name",
          );
          if (companyNameSetting) {
            companyName = companyNameSetting.value;
          }
        }
      } catch (e) {
        /* ignore */
      }

      const inv = this.receipt.invoice || {};
      

      const printContent = `
        <div class="receipt-container">
          <div class="header-section">
            <div class="company-name">${companyName}</div>
            <div class="doc-title">RECEIPT</div>
          </div>

          <div class="info-section">
            <table class="info-table">
              <tr>
                <td><strong>Receipt No:</strong> ${
                  this.receipt.receipt_no || ""
                }</td>
                <td><strong>Date:</strong> ${this.formatDate(
                  this.receipt.date,
                )}</td>
              </tr>
              <tr>
                <td><strong>Customer:</strong> ${this.customerName}</td>
                <td><strong>Status:</strong> ${
                  this.receipt.is_confirmed ? "Confirmed" : "Pending"
                }</td>
              </tr>
            </table>
          </div>

          <div class="info-section">
            <h3>Invoice Details</h3>
            <table class="info-table">
              <tr>
                <td><strong>Invoice No:</strong> ${
                  inv.invoice_number || ""
                }</td>
                <td><strong>Invoice Date:</strong> ${this.formatDate(
                  inv.invoice_date,
                )}</td>
              </tr>
              <tr>
                <td><strong>Branch:</strong> ${inv.branch_name || "-"}</td>
                <td><strong>Due Date:</strong> ${this.formatDate(
                  inv.invoice_due_date,
                )}</td>
              </tr>
            </table>
          </div>

          

          <div class="summary-section">
            <table class="summary-table">
              <tr>
                <td><strong>Invoice Total:</strong></td>
                <td style="text-align:right">${this.currencyCode} ${Number(
        inv.total_invoice || 0,
      ).toFixed(2)}</td>
              </tr>
              <tr class="paid-row">
                <td><strong>Amount Paid:</strong></td>
                <td style="text-align:right">${this.currencyCode} ${Number(
        this.receipt.amount,
      ).toFixed(2)}</td>
              </tr>
              <tr class="outstanding-row">
                <td><strong>Outstanding:</strong></td>
                <td style="text-align:right">${
                  this.currencyCode
                } ${this.outstandingAmount.toFixed(2)}</td>
              </tr>
            </table>
          </div>

          <div class="signature-section">
            <div class="signature-box">
              <div class="signature-line"></div>
              <div>Customer Signature</div>
            </div>
            <div class="signature-box">
              <div class="signature-line"></div>
              <div>Authorized Signature</div>
            </div>
          </div>

          <div class="footer">Thank you for your payment!</div>
        </div>
      `;

      const printWindow = window.open("", "_blank");
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>Print Receipt - ${this.receipt.receipt_no}</title>
            <style>
              body { font-family: Arial, sans-serif; padding: 20px; background: #f9f9f9; margin: 0; }
              .receipt-container { max-width: 800px; margin: 0 auto; background: #fff; border: 1px solid #ddd; border-radius: 8px; padding: 40px; box-shadow: 0 2px 10px rgba(0,0,0,0.1); }
              .header-section { text-align: center; margin-bottom: 30px; border-bottom: 2px solid #333; padding-bottom: 20px; }
              .company-name { font-size: 2em; font-weight: bold; margin-bottom: 10px; }
              .doc-title { font-size: 1.5em; letter-spacing: 2px; }
              .info-section { margin-bottom: 24px; }
              .info-section h3 { margin-bottom: 8px; font-size: 1.1em; border-bottom: 1px solid #eee; padding-bottom: 4px; }
              .info-table { width: 100%; border-collapse: collapse; }
              .info-table td { padding: 6px 0; }
              .items-section { margin-bottom: 24px; }
              .items-section h3 { margin-bottom: 8px; border-bottom: 1px solid #eee; padding-bottom: 4px; }
              .items-table { width: 100%; border-collapse: collapse; }
              .items-table th, .items-table td { border: 1px solid #ddd; padding: 8px; text-align: left; }
              .items-table th { background: #f5f5f5; }
              .summary-section { margin-bottom: 40px; }
              .summary-table { width: 100%; max-width: 400px; margin-left: auto; }
              .summary-table td { padding: 8px 0; }
              .paid-row td { font-size: 1.1em; }
              .outstanding-row td { font-size: 1.1em; color: #d32f2f; }
              .signature-section { display: flex; justify-content: space-around; margin-top: 60px; text-align: center; color: #888; }
              .signature-line { width: 200px; border-bottom: 1px solid #333; margin-bottom: 8px; display: inline-block; }
              .footer { text-align: center; margin-top: 40px; font-size: 0.9em; color: #666; }
              @media print { body { background: #fff; padding: 0; } .receipt-container { box-shadow: none; border: none; } }
            </style>
          </head>
          <body>${printContent}</body>
        </html>
      `);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => {
        printWindow.print();
      }, 250);
    },

    formatDate(date) {
      if (!date) return "";
      const d = new Date(date);
      if (isNaN(d.getTime())) return date;
      const day = String(d.getDate()).padStart(2, "0");
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const year = d.getFullYear();
      return `${day}/${month}/${year}`;
    },
  },
};
</script>

<style scoped>
.info-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}
.info-row .label {
  font-weight: 600;
  color: #666;
  min-width: 130px;
}
.info-row .value {
  font-size: 1.02em;
}
.summary-card {
  border: 1px solid #ddd;
  border-radius: 8px;
  background: white;
}
.summary-label {
  font-size: 0.9em;
  color: #666;
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 1px;
}
.summary-value {
  font-size: 1.5em;
  font-weight: bold;
}
</style>
